import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { STORAGE_KEYS } from '@/constants/constants';
import { Authority, UserAuthorities, UserSession } from '@/types/auth';

// Create a simple store for session storage changes
const createSessionStorageStore = (key: string) => {
  return {
    subscribe: (callback: () => void) => {
      const handler = (e: StorageEvent) => {
        if (e.key === key || e.key === null) {
          callback();
        }
      };
      window.addEventListener('storage', handler);
      
      // Also listen for custom events for same-window updates
      const customHandler = () => callback();
      window.addEventListener('sessionStorageChange', customHandler);
      
      return () => {
        window.removeEventListener('storage', handler);
        window.removeEventListener('sessionStorageChange', customHandler);
      };
    },
    getSnapshot: () => {
      return sessionStorage.getItem(key);
    },
    getServerSnapshot: () => {
      return null;
    }
  };
};

const userProfileStore = createSessionStorageStore(STORAGE_KEYS.USER_PROFILE);

/**
 * Hook to access user authorities from session storage
 * Provides utility methods to check permissions
 */
export function useAuthorities(): UserAuthorities {
  // Use useSyncExternalStore to reactively track sessionStorage changes
  const userProfileData = useSyncExternalStore(
    userProfileStore.subscribe,
    userProfileStore.getSnapshot,
    userProfileStore.getServerSnapshot
  );

  const authorities = useMemo(() => {
    try {
      if (!userProfileData) {
        return [];
      }

      const userSession: UserSession = JSON.parse(userProfileData);
      
      // Parse comma-separated authorities string
      if (userSession.authorities) {
        const parsedAuthorities = userSession.authorities
          .split(',')
          .map(auth => auth.trim())
          .filter(auth => auth.length > 0) as Authority[];
        
        return parsedAuthorities;
      }

      return [];
    } catch (error) {
      console.warn('Failed to parse user authorities:', error);
      return [];
    }
  }, [userProfileData]);

  const hasAuthority = useCallback((authority: Authority): boolean => {
    return authorities.includes(authority);
  }, [authorities]);

  const hasAnyAuthority = useCallback((...requiredAuthorities: Authority[]): boolean => {
    return requiredAuthorities.some(auth => authorities.includes(auth));
  }, [authorities]);

  const hasAllAuthorities = useCallback((...requiredAuthorities: Authority[]): boolean => {
    return requiredAuthorities.every(auth => authorities.includes(auth));
  }, [authorities]);

  /**
   * Check if user can view a resource based on naming convention
   * Example: canView('ADMIN_CAMPAIGN_MGMT') checks for ADMIN_CAMPAIGN_MGMT_VIEW
   */
  const canView = useCallback((resource: string): boolean => {
    const viewAuthority = `${resource}_VIEW` as Authority;
    return authorities.includes(viewAuthority);
  }, [authorities]);

  /**
   * Check if user can update a resource based on naming convention
   * Example: canUpdate('ADMIN_CAMPAIGN_MGMT') checks for ADMIN_CAMPAIGN_MGMT_UPDATE
   */
  const canUpdate = useCallback((resource: string): boolean => {
    const updateAuthority = `${resource}_UPDATE` as Authority;
    return authorities.includes(updateAuthority);
  }, [authorities]);

  return {
    authorities,
    hasAuthority,
    hasAnyAuthority,
    hasAllAuthorities,
    canView,
    canUpdate,
  };
}
